import { useMemo } from 'react';
import { TextInput } from '../../../components/TextInput';
import type { SampleDataFieldError, SampleDataTemplateContent } from '../../../types/sampleData';
import { CallsFieldset } from './CallsFieldset';
import { DuplicatePairFieldset } from './DuplicatePairFieldset';
import { EventsFieldset } from './EventsFieldset';
import { NotesFieldset } from './NotesFieldset';
import { PlayersFieldset } from './PlayersFieldset';
import { RosterBoardFieldset } from './RosterBoardFieldset';
import { SampleFieldset } from './SampleFormControls';
import { TeamsFieldset } from './TeamsFieldset';
import { buildFieldErrorLookup, fieldId } from './templateEditing';
import './SampleDataTemplateForm.css';

interface SampleDataTemplateFormProps {
  value: SampleDataTemplateContent;
  onChange: (next: SampleDataTemplateContent) => void;
  errors: SampleDataFieldError[];
}

/**
 * The sample data template editor: one fieldset per kind of record the seeder creates.
 *
 * Rows refer to each other by key (a call names a player), so the reference fields are pickers over
 * the rows above them rather than free text. Keys themselves never appear on screen.
 */
export const SampleDataTemplateForm = ({ value, onChange, errors }: SampleDataTemplateFormProps) => {
  const errorFor = useMemo(() => buildFieldErrorLookup(errors), [errors]);
  const section = { value, onChange, errorFor };

  return (
    // Not disabled while saving: that would drop focus from the field Enter was pressed in. Double
    // submits are blocked in the section instead, and saving never overwrites what is on screen.
    <div className="sample-form">
      <SampleFieldset legend="General">
        <TextInput
          id={fieldId('timeZone')}
          className="sample-form__field"
          label="Time zone"
          placeholder="America/New_York"
          value={value.timeZone}
          error={errorFor('timeZone')}
          data-testid="sample-data-time-zone"
          onChange={(e) => onChange({ ...value, timeZone: e.target.value })}
        />
      </SampleFieldset>
      <TeamsFieldset {...section} />
      <PlayersFieldset {...section} />
      <RosterBoardFieldset {...section} />
      <CallsFieldset {...section} />
      <NotesFieldset {...section} />
      <EventsFieldset {...section} />
      <DuplicatePairFieldset {...section} />
    </div>
  );
};
